/**
 * Error Handler Utilities
 * Centralized error handling for API responses
 */

import { AxiosError } from 'axios';
import { ApiResponseCode } from '../config/api.config';
import { ErrorResponse, ValidationError } from '../types/api.types';
import { logger } from './logger';

/**
 * Extract error message from various error types
 */
export const handleApiError = (error: unknown): string => {
  // Handle Axios errors
  if (error instanceof AxiosError) {
    const errorData = error.response?.data as ErrorResponse;

    // Handle specific API response codes
    if (errorData?.code) {
      switch (errorData.code) {
        case ApiResponseCode.UNAUTHORIZED:
          return 'Your session has expired. Please login again.';
        
        case ApiResponseCode.FORBIDDEN:
          return 'You don\'t have permission to perform this action.';
        
        case ApiResponseCode.NOT_FOUND:
          return errorData.message || 'The requested resource was not found.';
        
        case ApiResponseCode.BAD_REQUEST:
          return errorData.message || 'Invalid request. Please check your input.';
        
        case ApiResponseCode.VALIDATION_ERROR:
          return formatValidationErrors(errorData.errors);
        
        case ApiResponseCode.CONFLICT:
          return errorData.message || 'This resource already exists or conflicts with existing data.';
        
        case ApiResponseCode.INTERNAL_ERROR:
          return 'Something went wrong on our end. Please try again later.';
        
        default:
          return errorData.message || 'An error occurred. Please try again.';
      }
    }

    // Handle HTTP status codes when no API code present
    if (error.response?.status) {
      switch (error.response.status) {
        case 401:
          return 'Please login to continue.';
        case 403:
          return 'Access denied.';
        case 404:
          return 'Resource not found.';
        case 409:
          return 'Conflict with existing data.';
        case 500:
          return 'Server error. Please try again later.';
        default:
          return error.message || 'An error occurred.';
      }
    }

    // Handle network errors
    if (error.code === 'ECONNABORTED') {
      return 'Request timeout. Please check your internet connection and try again.';
    }

    if (error.code === 'ERR_NETWORK') {
      return 'Network error. Please check your internet connection.';
    }

    return error.message || 'An unexpected error occurred.';
  }

  // Handle RTK Query errors
  if (error && typeof error === 'object' && 'status' in error) {
    const rtkError = error as any;
    
    if (rtkError.data?.message) {
      return rtkError.data.message;
    }
    
    if (typeof rtkError.status === 'number') {
      switch (rtkError.status) {
        case 401:
          return 'Authentication required.';
        case 403:
          return 'Access denied.';
        case 404:
          return 'Not found.';
        default:
          return `Error: ${rtkError.status}`;
      }
    }
  }

  // Handle plain Error objects
  if (error instanceof Error) {
    return error.message;
  }

  // Handle string errors
  if (typeof error === 'string') {
    return error;
  }

  // Fallback
  return 'An unexpected error occurred. Please try again.';
};

/**
 * Format validation errors into readable message
 */
export const formatValidationErrors = (errors?: ValidationError[]): string => {
  if (!errors || errors.length === 0) {
    return 'Validation failed. Please check your input.';
  }

  if (errors.length === 1) {
    return errors[0]?.message || 'Validation error';
  }

  return errors.map(err => `${err.field}: ${err.message}`).join('\n');
};

/**
 * Check if error is an authentication error
 */
export const isAuthError = (error: unknown): boolean => {
  if (error instanceof AxiosError) {
    const errorData = error.response?.data as ErrorResponse;
    return (
      errorData?.code === ApiResponseCode.UNAUTHORIZED ||
      error.response?.status === 401
    );
  }
  return false;
};

/**
 * Check if error is a permission error
 */
export const isPermissionError = (error: unknown): boolean => {
  if (error instanceof AxiosError) {
    const errorData = error.response?.data as ErrorResponse;
    return (
      errorData?.code === ApiResponseCode.FORBIDDEN ||
      error.response?.status === 403
    );
  }
  return false;
};

/**
 * Check if error is a network error
 */
export const isNetworkError = (error: unknown): boolean => {
  if (error instanceof AxiosError) {
    return error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED';
  }
  return false;
};

/**
 * Log error for debugging (development only)
 */
export const logError = (error: unknown, context?: string): void => {
  if (import.meta.env.DEV) {
    logger.group(`❌ Error${context ? ` in ${context}` : ''}`, () => {
      logger.error(error);
      if (error instanceof AxiosError) {
        logger.log('Response:', error.response);
        logger.log('Config:', error.config);
      }
    });
  }
};
